<template>
  <section class="customer-documents-tab">
    <div class="customer-documents-tab__grid">
      <DocumentsTableCard
        :rows="documentsRows"
        :loading="loading"
        :show-download-all="showDownloadAllButton"
        @download-all="handleDownloadAll"
        @open-create="openCreateModal"
        @download-item="handleDownloadItem"
        @edit-item="handleEditItem($event, 'table')"
        @delete-item="handleDeleteItem"
      />

      <DocumentsLegalFileCard
        :records="legalRecords"
        :status-text="expedienteStatus.text"
        :status-variant="expedienteStatus.variant"
        :show-status-badge="showExpedienteStatusBadge"
        :show-report-button="showDownloadExpedienteButton"
        @download-item="handleDownloadItem"
        @edit-item="handleEditItem($event, 'legal')"
        @delete-item="handleDeleteItem"
        @download-report="handleDownloadExpediente"
      />
    </div>

    <DocumentsCreateModal
      :show="showCreateModal"
      :mode="documentModalMode"
      :initial-data="editingDocument"
      :show-name-as-text="documentModalSource === 'legal'"
      @close="showCreateModal = false"
      @submit="handleCreateDocument"
    />

    <ConfirmActionModal
      :show="isDeleteConfirmModalOpen"
      :title="text.confirmDelete.title"
      :subtitle="text.confirmDelete.subtitle"
      :message="deleteConfirmMessage"
      :confirm-text="text.confirmDelete.confirm"
      :cancel-text="text.confirmDelete.cancel"
      @cancel="closeDeleteConfirmModal"
      @confirm="confirmDeleteItem"
    />
  </section>
</template>

<script>
import DocumentsTableCard from '@/components/documents/DocumentsTableCard.vue';
import DocumentsLegalFileCard from '@/components/documents/DocumentsLegalFileCard.vue';
import DocumentsCreateModal from '@/components/documents/DocumentsCreateModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import customersService from '@/services/customers';
import {
  extractApiErrorMessage,
  extractPayload,
} from '@/utils/dataNormalization';
import { downloadBlobFile } from '@/utils/fileDownload';
import { formatDateShort } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  buildDocumentDownloadTimestamp,
  isPdfDocument,
  normalizeDocumentDownloadNameSegment,
  openDocumentInNewTab,
  resolveDocumentDownloadErrorMessage,
  resolveDocumentDownloadFileName,
  resolveDocumentFileName,
  resolveDocumentUrl,
  triggerDocumentDirectDownload,
} from '@/composables/documents/useDocumentsDownload';
import {
  closeDocumentDeleteModal,
  confirmDocumentDelete,
  openDocumentCreateModal,
  openDocumentDeleteModal,
  openDocumentEditModal,
  resetDocumentModalState,
  submitDocumentCrud,
} from '@/composables/documents/useDocumentsCrud';

const { catalog: text } = useDomainText('documents');

export default {
  name: 'CustomerDetailDocumentsTab',
  components: {
    DocumentsTableCard,
    DocumentsLegalFileCard,
    DocumentsCreateModal,
    ConfirmActionModal,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text,
      loading: false,
      documentsRows: [],
      legalRecords: [],
      recordComplete: 0,
      showCreateModal: false,
      documentModalMode: 'create',
      documentModalSource: 'table',
      editingDocument: null,
      isDeleteConfirmModalOpen: false,
      pendingDeleteDocument: null,
      deletingDocument: false,
    };
  },
  computed: {
    customerId() {
      return Number(this.customer?.id || 0);
    },
    expedienteStatus() {
      const isComplete = Number(this.recordComplete || 0) === 1;

      return isComplete
        ? { text: this.text.legalFileCard.status.complete, variant: 'complete' }
        : { text: this.text.legalFileCard.status.incomplete, variant: 'incomplete' };
    },
    deleteConfirmMessage() {
      const name = String(this.pendingDeleteDocument?.name || '').trim();
      return name !== ''
        ? this.text.confirmDelete.messageByName(name)
        : this.text.confirmDelete.messageDefault;
    },
    showExpedienteStatusBadge() {
      return this.legalRecords.length > 0;
    },
    showDownloadAllButton() {
      return this.documentsRows.some(
        (row) => Number(row?.isFile || 0) === 1 && String(row?.documentRaw || '').trim() !== ''
      );
    },
    showDownloadExpedienteButton() {
      return this.legalRecords.some((row) => row?.hasUploadedFile === true);
    },
    customerDownloadNameSegment() {
      return normalizeDocumentDownloadNameSegment(
        String(this.customer?.name || '').trim(),
        this.text.download.customerFallbackNameSegment
      );
    },
  },
  watch: {
    customerId: {
      immediate: true,
      handler() {
        this.loadDocuments();
      },
    },
    showCreateModal(visible) {
      if (visible) {
        return;
      }

      resetDocumentModalState(this, { source: 'table' });
    },
  },
  methods: {
    openCreateModal() {
      openDocumentCreateModal(this, { source: 'table' });
    },
    mapDocumentRow(row) {
      const comments = String(row?.description || '').trim();

      return {
        id: Number(row?.id || 0),
        name: String(row?.name || this.text.common.blankValue).trim() || this.text.common.blankValue,
        validityStart: formatDateShort(row?.start_date, { fallback: this.text.common.blankValue }),
        validityEnd: formatDateShort(row?.end_date, { fallback: this.text.common.blankValue }),
        comments,
        isFile: Number(row?.is_file || 0) === 1 ? 1 : 0,
        recordId: Number(row?.record_id || 0),
        documentRaw: String(row?.document || '').trim(),
        documentUrl: String(row?.document_url || '').trim(),
      };
    },
    mapLegalRecordRow(row) {
      const document = row?.document && typeof row.document === 'object' ? row.document : null;
      const documentRaw = String(document?.document || '').trim();
      const documentUrl = String(document?.document_url || '').trim();
      const hasDocument = Number(document?.id || 0) > 0;
      const hasUploadedFile = hasDocument && Number(document?.is_file || 0) === 1 && documentRaw !== '';
      const comments = String(document?.description || '').trim();
      const displayComments = comments !== '' ? comments : '';

      return {
        key: `record-${Number(row?.id || 0)}`,
        id: Number(document?.id || 0),
        fileName: String(row?.name || this.text.common.blankValue).trim() || this.text.common.blankValue,
        validityStart: formatDateShort(document?.start_date, { fallback: this.text.common.blankValue }),
        validityEnd: formatDateShort(document?.end_date, { fallback: this.text.common.blankValue }),
        comments,
        displayComments,
        isFile: Number(document?.is_file || 0) === 1 ? 1 : 0,
        recordId: Number(row?.id || 0),
        documentRaw,
        name: String(document?.name || row?.name || this.text.common.blankValue).trim() || this.text.common.blankValue,
        documentUrl,
        hasDocument,
        hasUploadedFile,
      };
    },
    async loadDocuments() {
      const customerId = this.customerId;
      if (customerId <= 0) {
        this.documentsRows = [];
        this.legalRecords = [];
        this.recordComplete = 0;
        return;
      }

      this.loading = true;
      try {
        const response = await customersService.documentsIndex(customerId);
        const payload = extractPayload(response);
        const documents = Array.isArray(payload?.documents) ? payload.documents : [];
        const records = Array.isArray(payload?.records) ? payload.records : [];

        this.documentsRows = documents
          .map((row) => this.mapDocumentRow(row))
          .filter((row) => Number(row?.recordId || 0) <= 0);
        this.legalRecords = records.map((row) => this.mapLegalRecordRow(row));
        this.recordComplete = Number(payload?.record_complete || 0) === 1 ? 1 : 0;
      } catch (error) {
        this.documentsRows = [];
        this.legalRecords = [];
        this.recordComplete = 0;
        this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.loadCustomerError));
      } finally {
        this.loading = false;
      }
    },
    async handleDownloadAll() {
      if (this.customerId <= 0) {
        return;
      }

      try {
        const response = await customersService.documentsDownloadAll(this.customerId);

        const fileName = resolveDocumentDownloadFileName(
          response,
          `${this.text.download.customerPrefix}_${this.customerDownloadNameSegment}_${buildDocumentDownloadTimestamp()}.zip`
        );
        downloadBlobFile(response, fileName, 'application/zip');
        this.$toast?.success(this.text.toasts.downloadStarted);
      } catch (error) {
        const message = await resolveDocumentDownloadErrorMessage(error, this.text.toasts.noDocumentsToDownload);
        this.$toast?.error(message);
      }
    },
    async handleDownloadItem(row) {
      const url = resolveDocumentUrl(row);
      if (url === '') {
        this.$toast?.info(this.text.toasts.noDownloadableFile);
        return;
      }

      if (isPdfDocument(row)) {
        openDocumentInNewTab(url);
        return;
      }

      await triggerDocumentDirectDownload(url, resolveDocumentFileName(row));
    },
    handleEditItem(row, source = 'table') {
      openDocumentEditModal(this, row, { source: source === 'legal' ? 'legal' : 'table' });
    },
    async handleDownloadExpediente() {
      if (this.customerId <= 0) {
        return;
      }

      try {
        const response = await customersService.documentsDownloadLegalFile(this.customerId);
        const fileName = resolveDocumentDownloadFileName(
          response,
          `${this.text.download.legalFilePrefix}_${this.customerDownloadNameSegment}_${buildDocumentDownloadTimestamp()}.zip`
        );
        downloadBlobFile(response, fileName, 'application/zip');
        this.$toast?.success(this.text.toasts.downloadStarted);
      } catch (error) {
        const message = await resolveDocumentDownloadErrorMessage(error, this.text.toasts.noDocumentsToDownload);
        this.$toast?.error(message);
      }
    },
    handleDeleteItem(row) {
      openDocumentDeleteModal(this, row);
    },
    closeDeleteConfirmModal() {
      closeDocumentDeleteModal(this);
    },
    async confirmDeleteItem() {
      const documentId = Number(this.pendingDeleteDocument?.id || 0);
      if (this.customerId <= 0 || documentId <= 0) {
        this.closeDeleteConfirmModal();
        return;
      }

      this.deletingDocument = true;
      try {
        const result = await confirmDocumentDelete({
          parentId: this.customerId,
          documentId,
          deleteRequest: (customerId, currentDocumentId) => customersService.documentsDelete(customerId, currentDocumentId),
          toast: this.$toast,
          successMessage: this.text.toasts.deleteSuccess,
          errorMessage: this.text.toasts.deleteError,
        });

        if (!result.ok) {
          return;
        }

        this.closeDeleteConfirmModal();
        await this.loadDocuments();
      } finally {
        this.deletingDocument = false;
      }
    },
    async handleCreateDocument(payload) {
      if (this.customerId <= 0) {
        return;
      }

      const name = String(payload?.name || '').trim();
      const sourceType = String(payload?.sourceType || 'file').trim();
      const description = String(payload?.comments || '').trim();
      const startDate = String(payload?.validityStart || '').trim();
      const endDate = String(payload?.validityEnd || '').trim();
      const recordId = Number(this.editingDocument?.recordId || 0);

      const result = await submitDocumentCrud({
        mode: this.documentModalMode,
        editingDocument: this.editingDocument,
        payload: {
          ...payload,
          name,
          sourceType,
          comments: description,
          validityStart: startDate,
          validityEnd: endDate,
        },
        recordId,
        updateRequest: (documentId, updatePayload) => customersService.documentsUpdate(this.customerId, documentId, updatePayload),
        createRequest: (formData, config) => customersService.documentsStore(this.customerId, formData, config),
        toast: this.$toast,
        toasts: this.text.toasts,
      });

      if (!result.ok) {
        return;
      }

      try {
        this.showCreateModal = false;
        resetDocumentModalState(this, { source: 'table' });
        await this.loadDocuments();
      } catch (error) {
        this.$toast?.error(extractApiErrorMessage(error, this.text.toasts.loadCustomerError));
      }
    },
  },
};
</script>

<style scoped>
.customer-documents-tab {
  width: 100%;
}

.customer-documents-tab__grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

@media (max-width: 1200px) {
  .customer-documents-tab__grid {
    grid-template-columns: 1fr;
  }
}
</style>
